import React from 'react';
import { 
  Box, 
  Stack, 
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Alert,
  MenuItem,
  Typography
} from '@mui/material';
import { 
  Home as HomeIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Apartment as ApartmentIcon,
  CalendarToday as CalendarIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  AspectRatio as SquareFootIcon,
  AccountBalance as HOAIcon
} from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface PropertyInfoStepProps {
  stepProgress: number;
}

const PropertyInfoStep: React.FC<PropertyInfoStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  
  // Use the progress passed from the main LeaseForm component
  const progress = stepProgress;
  
  const hasHOA = watch('property.hasHOA');
  
  const propertyTypes = [
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'Single Family House' },
    { value: 'condo', label: 'Condominium' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'duplex', label: 'Duplex' },
    { value: 'studio', label: 'Studio' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <StepFormLayout
      title="Property Details"
      description="Information about the rental property"
      icon={<HomeIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={progress}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>🏠 Tip:</strong> Make sure the address matches official documents like deeds or HOA records for legal accuracy.
        </Typography>
      </Alert>
      
      {/* Property Address */}
      <FormSection
        title="Rental Property Address"
        icon={<LocationIcon color="primary" />}
        tooltip="The exact address where tenants will live"
        isRequired={true}
        defaultExpanded={true}
      >
        <Stack spacing={3}>
          <FormTextField
            name="property.address.street"
            control={control}
            label="Street Address"
            placeholder="123 Main Street"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormTextField
            name="property.address.unit"
            control={control}
            label="Unit/Apartment Number"
            placeholder="Apt 2B, Unit 101, etc. (if applicable)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ApartmentIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, 
            gap: 3 
          }}>
            <FormTextField
              name="property.address.city"
              control={control}
              label="City"
              placeholder="Los Angeles"
              required
            />
            <FormTextField
              name="property.address.state"
              control={control}
              label="State"
              placeholder="CA"
              required
              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            />
            <FormTextField
              name="property.address.zip"
              control={control}
              label="ZIP Code"
              placeholder="90210"
              required
              inputProps={{ maxLength: 10 }}
            />
          </Box>
        </Stack>
      </FormSection>
      
      {/* Property Specifications */}
      <FormSection
        title="Property Specifications"
        icon={<ApartmentIcon color="primary" />}
        isRequired={true}
        defaultExpanded={false}
      >
        <Stack spacing={3}>
          <FormSelect
            name="property.type"
            control={control}
            label="Property Type"
            required
            fullWidth
          >
            {propertyTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </FormSelect>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            <FormTextField
              name="property.yearBuilt"
              control={control}
              label="Year Built"
              type="number"
              placeholder="2020"
              required
              inputProps={{ min: 1800, max: new Date().getFullYear() }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              name="property.bedrooms"
              control={control}
              label="Bedrooms"
              type="number"
              placeholder="2"
              required
              inputProps={{ min: 0, max: 20 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BedIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              name="property.bathrooms"
              control={control}
              label="Bathrooms"
              type="number"
              placeholder="1.5"
              required
              inputProps={{ min: 0.5, max: 20, step: 0.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BathtubIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              name="property.squareFootage"
              control={control}
              label="Square Feet"
              type="number"
              placeholder="850"
              inputProps={{ min: 100, max: 50000 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SquareFootIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>
      </FormSection>
      
      {/* Additional Details */}
      <FormSection
        title="Additional Details"
        tooltip="Property description and HOA information"
        icon={<InfoIcon color="primary" />}
        isRequired={false}
        defaultExpanded={false}
      >
        <Stack spacing={3}>
            <FormTextField
              name="property.description"
              control={control}
              label="Property Description"
              multiline
              rows={3}
              placeholder="Describe unique features, recent updates, neighborhood highlights..."
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <HOAIcon color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                HOA Information
              </Typography>
            </Box>
            
            <Controller
              name="property.hasHOA"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox 
                      {...field} 
                      checked={field.value}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Property is subject to HOA
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Homeowners Association rules and fees apply
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
            
            {hasHOA && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'primary.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.200'
              }}>
                <Stack spacing={2}>
                  <FormTextField
                    name="property.hoa.name"
                    control={control}
                    label="HOA Name"
                    placeholder="Sunset Gardens HOA"
                  />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                    gap: 2 
                  }}>
                    <FormTextField
                      name="property.hoa.contactInfo"
                      control={control}
                      label="HOA Contact"
                      placeholder="Phone or email"
                    />
                    <FormTextField
                      name="property.hoa.monthlyFee"
                      control={control}
                      label="Monthly HOA Fee"
                      type="number"
                      placeholder="150"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
        </Stack>
      </FormSection>
      

      </Box>
    </StepFormLayout>
  );
};

export default PropertyInfoStep; 